
import { IStaffPayrollPrintData } from 'features/report/services/types/reportStaff';
import { IParamsReportSalaryStaff } from 'features/report/services/types/review';
import { ESalaryPayrollType } from 'features/settingStaff/services/constants';
import { useMemo } from 'react';
import shopSelectors from 'services/shop/selectors';
import { EHideFieldReport, isHideFieldReport } from 'utils/hide-field-report';
import { IUIStaffFieldItem } from '../StaffPayrollDetail';

const useSimplePayrollTemplate = (params: IParamsReportSalaryStaff) => {
  const allSetting = shopSelectors.data.allSetting();
  const listHideFieldReport = shopSelectors.getListHideFieldReport();
  const staffPayrollFields: IUIStaffFieldItem<IStaffPayrollPrintData>[] = useMemo(() => {
    const cashLabel = allSetting?.labelSalaryCashOrBonus || 'Cash';
    const singleStaff = !!params.staffId;
    return [
      {
        label: 'TOTAL SALES',
        fieldName: 'totalSales',
        component: 'ReportPayrollTitleRow',
        hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.TOTAL_SALE)
          || !data?.totalSales,
      },
      {
        label: 'SUPPLY FEES',
        fieldName: data => -(data?.supplyFee || 0),
        component: 'ReportPayrollTitleRow',
        hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.SUPPLY_FEE)
          || !data?.supplyFee,
      },
      {
        label: 'TOTAL',
        fieldName: (data) => (data?.totalSaleFinal || 0),
        component: 'ReportPayrollTitleRow',
        hidden: isHideFieldReport(listHideFieldReport, EHideFieldReport.TOTAL),
        className: 'TOTAL_ROW',
      },
      {
        label: 'PAYMENT TYPES',
        fieldName: () => ' ',
        hideValue: true,
        component: 'ReportPayrollCaption2HighLightRow',
        hidden: isHideFieldReport(listHideFieldReport, EHideFieldReport.PAYMENT_TYPE) || !singleStaff,
        fields: [
          {
            label: 'Discounts',
            fieldName: 'totalDiscount',
            component: 'ReportPayrollPaymentRow',
          },
          {
            label: 'Credit Card',
            fieldName: 'creditAmount',
            component: 'ReportPayrollPaymentRow',
          },
          {
            label: 'Cash',
            fieldName: 'cashAmount',
            component: 'ReportPayrollPaymentRow',
          },
          {
            label: 'Gift Card',
            fieldName: 'giftCardAmount',
            component: 'ReportPayrollPaymentRow',
          },
          {
            label: 'Loyalty',
            fieldName: 'loyaltyAmount',
            component: 'ReportPayrollPaymentRow',
            hidden: (data) => !data?.loyaltyAmount,
          },
          {
            label: 'Check',
            fieldName: 'checkAmount',
            component: 'ReportPayrollPaymentRow',
            hidden: (data) => !data?.checkAmount,
          },
          {
            label: 'Other',
            fieldName: 'otherAmount',
            component: 'ReportPayrollPaymentRow',
          },
        ]
      },
      {
        label: (data) => {
          const hideCommissionRate = isHideFieldReport(
            listHideFieldReport,
            EHideFieldReport.PERCENT_COMMISSION
          );
          return hideCommissionRate
            ? 'COMMISSION'
            : `COMMISSION (${data?.commissionPercent}%)`;
        },
        fieldName: 'totalCommission',
        component: 'ReportPayrollTitleRow',
        hidden: (data) =>
          isHideFieldReport(listHideFieldReport, EHideFieldReport.COMMISSION)
          || !data?.totalCommission,
        className: 'COMMISSION_ROW',
      },
      {
        label: 'DISCOUNT',
        fieldName: 'staffDiscount',
        component: 'ReportPayrollTitleRow',
        hidden: (data) => isHideFieldReport(listHideFieldReport, EHideFieldReport.DISCOUNT),
      },
      {
        hidden: isHideFieldReport(listHideFieldReport, EHideFieldReport.TIP),
        fields: [
          {
            label: 'COLLECTED TIPS',
            fieldName: 'tipCollected',
            component: 'ReportPayrollTipFeeRow',
          },
          {
            label: (data) => `TIP FEE ${(data?.tipFeeType === 'PERCENT' ? `(${data?.tipFeeValue || 0}%)` : '')}`,
            fieldName: data => -(data?.tipFee || 0),
            component: 'ReportPayrollTipFeeRow',
            hidden: (data) => !data?.tipFee,
          },
          {
            label: 'TOTAL TIP',
            fieldName: 'totalTip',
            component: 'ReportPayrollTipFeeRow',
            hidden: (data) => !data?.tipFee,
          },
        ]
      },
      {
        label: 'CLEANING FEE',
        fieldName: data => -(data?.totalFeeCleaning || 0),
        component: 'ReportPayrollTipFeeRow',
        hidden: (data) => !data?.totalFeeCleaning,
      },
      {
        label: 'SUPPLY SHARE',
        fieldName: 'supplyShareFee',
        component: 'ReportPayrollTipFeeRow',
        hidden: (data) => !data?.supplyShareFee,
      },
      {
        bt: 'print_line',
        hidden: (data) => data?.payrollType !== ESalaryPayrollType.W2,
        fields: [
          {
            label: (data) => `Federal Tax Withheld (${data?.tax?.federalTaxPercent || 0}%)`,
            fieldName: ['tax', 'federalTax'],
            component: 'ReportPayrollCaption3Row',
            hidden: (data) => !data?.tax?.federalTax,
          },
          {
            label: (data) => `Social Security Tax Withheld (${data?.tax?.socialTaxPercent || 0}%)`,
            fieldName: ['tax', 'socialTax'],
            component: 'ReportPayrollCaption3Row',
            hidden: (data) => !data?.tax?.socialTax,
          },
          {
            label: (data) => `Medicare Tax Withheld (${data?.tax?.medicalTaxPercent || 0}%)`,
            fieldName: ['tax', 'medicalTax'],
            component: 'ReportPayrollCaption3Row',
            hidden: (data) => !data?.tax?.medicalTax,
          }
        ]
      },
      {
        label: 'PAYROLL',
        fieldName: 'totalPayroll',
        component: 'ReportPayrollCaption2HighLightRow',
      }
    ] as IUIStaffFieldItem<IStaffPayrollPrintData>[];
  }, [params, params.staffId, allSetting, listHideFieldReport]);

  return staffPayrollFields;
};

export default useSimplePayrollTemplate;